'use client'

import { ChevronLeft, ChevronRight } from 'lucide-react'
import React from 'react'

import { cn } from '@/utilities/ui'

type BlogPostsPaginationProps = {
  className?: string
  currentPage: number
  isLoading?: boolean
  nextLabel: string
  onNext?: () => void
  onPageChange?: (page: number) => void
  onPrevious?: () => void
  paginationAriaLabel: string
  previousLabel: string
  totalPages: number
}

const navButtonClassName =
  'inline-flex size-10 items-center justify-center rounded-full bg-bluetext text-white transition-colors hover:bg-bluetext/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-40'

const pageButtonClassName =
  'inline-flex size-10 items-center justify-center rounded-full font-outfit text-base font-medium leading-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-40'

export function BlogPostsPagination({
  className,
  currentPage,
  isLoading = false,
  nextLabel,
  onNext,
  onPageChange,
  onPrevious,
  paginationAriaLabel,
  previousLabel,
  totalPages,
}: BlogPostsPaginationProps) {
  const page = Number(currentPage)
  const pageCount = Math.max(1, Number(totalPages))
  const pages = Array.from({ length: pageCount }, (_, index) => index + 1)
  const showPrevious = page > 1
  const showNext = page < pageCount

  return (
    <nav
      aria-label={paginationAriaLabel}
      className={cn('flex w-full justify-center', className)}
    >
      <ul className="flex items-center gap-3">
        {showPrevious ? (
          <li>
            <button
              aria-label={previousLabel}
              className={navButtonClassName}
              disabled={isLoading}
              onClick={() => {
                if (!isLoading) {
                  onPrevious?.()
                }
              }}
              type="button"
            >
              <ChevronLeft aria-hidden className="size-5" />
            </button>
          </li>
        ) : null}

        {pages.map((pageNumber) => {
          const isActive = pageNumber === page

          return (
            <li key={pageNumber}>
              <button
                aria-current={isActive ? 'page' : undefined}
                className={cn(
                  pageButtonClassName,
                  isActive
                    ? 'bg-turquoise-light text-dark-800/80 shadow-[0_6px_20px_rgba(9,114,82,0.12)]'
                    : 'border border-black/10 bg-white/80 text-dark-800/80 hover:bg-white',
                )}
                disabled={isLoading}
                onClick={() => {
                  if (!isActive && !isLoading) {
                    onPageChange?.(pageNumber)
                  }
                }}
                type="button"
              >
                {pageNumber}
              </button>
            </li>
          )
        })}

        {showNext ? (
          <li>
            <button
              aria-label={nextLabel}
              className={navButtonClassName}
              disabled={isLoading}
              onClick={() => {
                if (!isLoading) {
                  onNext?.()
                }
              }}
              type="button"
            >
              <ChevronRight aria-hidden className="size-5" />
            </button>
          </li>
        ) : null}
      </ul>
    </nav>
  )
}
